// src/components/questionanswer/QnAJava.jsx
import React, { useEffect, useMemo, useState } from "react";
import juniorQuestions from "./qnaJSONdata/QnAJuniorJava.json";
import seniorQuestions from "./qnaJSONdata/QnASeniorJava.json";
import java8Questions from "./qnaJSONdata/QnAJava8Features.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnAJava + Download PDF
 * - LEFT: sticky "On this page" + sponsored ad
 * - MAIN: inline questions with filter; header includes Download PDF CTA
 * - RIGHT: upgraded sidebar (already has ad card)
 */

// ============== AdSense helpers (unchanged) ==============
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // your client id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ============== Page bits ==============
const normalize = (s = "") => s.toLowerCase();

// You can override via .env: REACT_APP_JAVA_PDF_URL=https://cdn.yoursite.com/java.pdf
const JAVA_PDF_URL =
  process.env.REACT_APP_JAVA_PDF_URL ||
  `${process.env.PUBLIC_URL || ""}/pdfs/java-interview-questions.pdf`;

function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 380 }}
        type="search"
        placeholder="Filter questions (e.g., collections, JVM, streams)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft() {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#junior">Junior Q&amp;A</a>
          <a className="nav-link px-0" href="#senior">Senior Q&amp;A</a>
          <a className="nav-link px-0" href="#java8">Java 8 Q&amp;A</a>
          {/* NEW: quick download link */}
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={JAVA_PDF_URL}
            download="TechVenture-Java-Interview-Questions.pdf"
            aria-label="Download Java Q&A as PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

// ============== Main component ==============
export default function QnAJava() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const totalCount =
    juniorQuestions.length + seniorQuestions.length + java8Questions.length;

  const filteredJunior = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return juniorQuestions;
    return juniorQuestions.filter((x) =>
      normalize(x.question + " " + x.answer).includes(qn)
    );
  }, [query]);

  const filteredSenior = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return seniorQuestions;
    return seniorQuestions.filter((x) =>
      normalize(x.question + " " + x.answer).includes(qn)
    );
  }, [query]);

  const filteredJava8 = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return java8Questions;
    return java8Questions.filter((x) =>
      normalize(x.question + " " + x.answer).includes(qn)
    );
  }, [query]);

  const shownCount =
    filteredJunior.length + filteredSenior.length + filteredJava8.length;

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Ad */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          {/* Header with Download PDF CTA */}
          <header className="mb-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h1 className="h2 mb-1">Java Interview Questions</h1>
                <p className="text-muted mb-0">
                  Curated questions for junior and senior roles. Use the filter to jump to what you need.
                </p>
              </div>
              <div className="btn-group">
                <a
                  href={JAVA_PDF_URL}
                  className="btn btn-outline-danger btn-sm"
                  download="TechVenture-Java-Interview-Questions.pdf"
                  aria-label="Download Java Interview Questions PDF"
                >
                  Download PDF
                </a>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => window.print()}
                  aria-label="Print this page"
                >
                  Print
                </button>
              </div>
            </div>
          </header>

          <FilterBar value={query} onChange={setQuery} total={totalCount} shown={shownCount} />

          {/* Junior section */}
          <section id="junior" className="pt-2">
            <h2 className="h4 mt-3">Top Interview Questions for Entry-Level Java Developers</h2>
            <p className="text-body-secondary">
              Java is an object-oriented, class-based language known for portability (JVM), strong libraries, and built-in memory management. These questions cover essentials you should master for junior roles.
            </p>
            <QAInlineList items={filteredJunior} />
          </section>

          {/* In-article ad */}
          <div className="my-3 text-center">
            <AdSlot adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
          </div>

          {/* Senior section */}
          <section id="senior" className="pt-2">
            <h2 className="h4 mt-4">Top Interview Questions for Senior Java Developers</h2>
            <p className="text-body-secondary">Deep-dive topics across JVM internals, concurrency, performance, and distributed systems.</p>
            <QAInlineList items={filteredSenior} />
          </section>

          {/* Java 8 section */}
          <section id="java8" className="pt-2">
            <h2 className="h4 mt-4">Java 8 Interview Questions</h2>
            <p className="text-body-secondary">The most popular Java 8 Features interview questions are as follows:</p>
            <QAInlineList items={filteredJava8} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Pro tip: Bookmark tough questions and revisit them weekly.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
